import { isNil } from 'lodash';
import { useEffect, useCallback } from 'react';

type MessageType = 'login' | 'logout';

interface IframeMessage<T> {
  type: MessageType;
  data: T;
}

/**
 * Use this hook to send and receive messages between the parent and child windows.
 * @param messageType - The type of message to send.
 * @param messageHandler - The handler function to process the message.
 * @returns An object with a broadcast function to send messages and
 *  a messageHandler function to process incoming messages.
 */
const useBroadcastIframe = <T>({
  messageType,
  messageHandler,
}: {
  messageType: MessageType;
  messageHandler: (data: T) => void;
}) => {
  const broadcast = useCallback(
    (data: T) => {
      // send message to opener window (popup case)
      if (window.opener) {
        window.opener.postMessage({ type: messageType, data }, '*');
      }

      // send message to parent window (iframe case)
      if (window.parent !== window) {
        window.parent.postMessage({ type: messageType, data }, '*');
      }
    },
    [messageType],
  );

  /**
   * Handle incoming messages from the parent or child window.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent<IframeMessage<T>>) => {
      // Check if event.data exists and has the expected shape
      if (isNil(event.data)) {
        return;
      }

      // Only handle messages with matching type
      if (event.data.type === messageType) {
        messageHandler(event.data.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [messageType, messageHandler]);

  return { broadcast };
};

export default useBroadcastIframe;
